/**
 * Deep link handler for mobile SAML authentication.
 *
 * After SAML auth completes, the server redirects to
 * knocknoc://auth-callback?token=<one-time-token>. Android catches this via
 * the intent filter and fires the appUrlOpen event. This module listens for
 * that event, exchanges the token for a session cookie, and navigates to /home.
 */

import { isNativePlatform } from "./platform";
import { getServerUrl } from "./server-url";
import { setSessionToken } from "./session-token";
import { maybeShowBatteryOptimizationPrompt } from "./battery-optimization";
import { exchangeAuthToken } from "../api/auth";
import { fetchUserStatus } from "../queries/session";
import type { Router } from "vue-router";
import type { PluginListenerHandle } from "@capacitor/core";

const AUTH_CALLBACK_PREFIX = "knocknoc://auth-callback";
const SAML_AUTH_TIMEOUT_MS = 120_000; // 2 minutes

let _router: Router | null = null;
let _listenerHandle: PluginListenerHandle | null = null;
let _samlAuthInProgress = false;
let _samlTimeout: ReturnType<typeof setTimeout> | null = null;
let _handlingCallback = false;

/**
 * Call before opening the SAML browser tab. Suppresses competing status
 * fetches that would otherwise fire when the app resumes from the browser.
 */
export function setSamlAuthPending(): void {
  _samlAuthInProgress = true;
  if (_samlTimeout) clearTimeout(_samlTimeout);
  _samlTimeout = setTimeout(() => {
    _samlAuthInProgress = false;
    _samlTimeout = null;
  }, SAML_AUTH_TIMEOUT_MS);
}

/** Returns true while a SAML auth flow is in progress. */
export function isSamlAuthInProgress(): boolean {
  return _samlAuthInProgress;
}

function clearSamlAuthPending(): void {
  _samlAuthInProgress = false;
  if (_samlTimeout) {
    clearTimeout(_samlTimeout);
    _samlTimeout = null;
  }
}

/**
 * Initialise the deep link listener for SAML auth callbacks.
 * Call once from main-user.ts after the app mounts.
 */
export async function initAuthCallback(router: Router): Promise<void> {
  if (!isNativePlatform()) return;
  _router = router;

  const { App } = await import("@capacitor/app");

  // Handle deep links received while the app is running
  _listenerHandle = await App.addListener("appUrlOpen", (event) => {
    if (event.url.startsWith(AUTH_CALLBACK_PREFIX)) {
      handleAuthCallback(event.url);
    }
  });

  // Handle the case where the app was killed and relaunched via the deep link
  const launchUrl = await App.getLaunchUrl();
  if (launchUrl?.url?.startsWith(AUTH_CALLBACK_PREFIX)) {
    handleAuthCallback(launchUrl.url);
  }
}

export async function removeAuthCallbackListener(): Promise<void> {
  if (_listenerHandle) {
    await _listenerHandle.remove();
    _listenerHandle = null;
  }
}

async function handleAuthCallback(url: string): Promise<void> {
  if (_handlingCallback) return;
  _handlingCallback = true;

  try {
    const parsed = new URL(url);
    const errorKey = parsed.searchParams.get("error");
    const token = parsed.searchParams.get("token");

    // Server-side SAML failure path: the backend sends
    // knocknoc://auth-callback?error=<i18n key> for mobile flows so the
    // Custom Tab never renders the web login page. Close the browser and
    // push to Login with the error query so LoginView.onMounted surfaces it.
    if (errorKey) {
      console.log(`[AuthCallback] SAML error: ${errorKey}`);
      try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.close();
      } catch {
        // Browser might already be closed.
      }
      clearSamlAuthPending();
      if (_router) {
        await _router.push({ name: "Login", query: { error: errorKey } });
      }
      return;
    }

    if (!token) {
      console.log("[AuthCallback] No token in callback URL");
      return;
    }

    const serverUrl = await getServerUrl();
    if (!serverUrl) return;

    const exchanged = await exchangeAuthToken(token);
    // Native callers get { sessionToken }, sent later as a bearer. Web callers
    // get an empty body and use the cookie.
    const sessionToken = exchanged?.sessionToken;
    if (typeof sessionToken === "string" && sessionToken) {
      await setSessionToken(sessionToken);
    }

    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.close();
    } catch {
      // Browser might already be closed
    }

    if (_router) {
      // Outside setup, so useUserSession() is not available.
      await fetchUserStatus({ force: true });
      // As signInUser does, ask for a battery optimisation exemption so
      // background IP polling survives OEM power management.
      maybeShowBatteryOptimizationPrompt().catch(() => {});
      clearSamlAuthPending();
      await _router.push("/home");
    } else {
      clearSamlAuthPending();
    }
  } catch (e) {
    console.log(`[AuthCallback] Token exchange failed: ${e}`);
    clearSamlAuthPending();
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.close();
    } catch {
      // ignore
    }
  } finally {
    _handlingCallback = false;
  }
}
